<script>
import {
    fade
} from "svelte/transition";
import {
    modalState, hideModal
} from "@app/store/modalStore.svelte.js";
import { toast } from '@zerodevx/svelte-toast';
import {authState, updateTeam, updateUser} from '@app/store/authStore.svelte.js';
import * as m from '@app/paraglide/messages.js';
import {
    BarLoader
} from 'svelte-loading-spinners';
import { auth, functions } from '@app/configs/firebase.js';
import { httpsCallable } from 'firebase/functions';
import db from '@app/configs/firestore.js';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';

import {v4 as uuidv4} from 'uuid';

let email = auth.currentUser.email,
    uid = auth.currentUser.uid,
    selectedUser = $state({}),
    users = $state([]),
    dbWork = $state();

// New state variables for groups, roles, and active tab
let selectedGroup = $state({}),
    groups = $state([]),
    activeTab = $state('users'),
    availableRoles = $state(['admin', 'manager', 'editor', 'viewer']),
    loadingUserOperations = $state({}),  // Tracks loading state for add/remove user operations
    loadingPluginOperations = $state({});  // Tracks loading state for plugin enable/disable operations

function getRoleLabel(role) {
    switch (role) {
        case 'admin':
            return m.team_manager_role_admin();
        case 'manager':
            return m.team_manager_role_manager();
        case 'editor':
            return m.team_manager_role_editor();
        case 'viewer':
            return m.team_manager_role_viewer();
        default:
            return role;
    }
}

function formatUserName(user) {
    return user.firstName ? `${user.firstName} ${user.lastName}` : user.email;
}

function formatUserOptionLabel(user) {
    const statusSuffix = user.disable ? ` (${m.team_manager_disabled()})` : '';
    return m.team_manager_user_option_label({
        name: formatUserName(user),
        role: getRoleLabel(user.role),
        status: statusSuffix
    });
}

function sortUsers(){
    users.sort((a, b) => {
        if (a.firstName){
            if (b.firstName){
                return a.firstName.localeCompare(b.firstName, undefined, {sensitivity: 'base'})
            } else {
                return a.firstName.localeCompare(b.email, undefined, {sensitivity: 'base'})
            }
        } else {
            if (b.firstName){
                return a.email.localeCompare(b.firstName, undefined, {sensitivity: 'base'})
            } else {
                return a.email.localeCompare(b.email, undefined, {sensitivity: 'base'})
            }
        }        
    });
}

function sortGroups() {
    groups.sort((a, b) => {
        return a.name.localeCompare(b.name, undefined, {sensitivity: 'base'});
    });
}

async function createTeam(){
    try {
        dbWork = await httpsCallable(functions, 'v8CreateTeam')({
            id : uuidv4(),
            owner : email,
            ownerId : uid,
            members : [email],
        });

        console.log("Create Team Data", dbWork.data);
        updateTeam(dbWork.data.team);
        updateUser(dbWork.data.users[0]);

        users = dbWork.data.users;
        sortUsers();
        toast.push(m.team_manager_team_created_success(), {classes: ['toast-success']});
    } catch(err){
        console.log(m.team_manager_create_team_failed_log());
        console.error(err,err.message);
        toast.push(m.team_manager_create_team_failed({ error: err.message }), {classes: ['toast-danger']});
    }    
}

async function createUser(){
    users = [...users, {
        id : uuidv4(),
        firstName : "",
        lastName : "",
        email : "",
        admin : false,
        disable : false,
        role : "editor",
        groups: [],
        new : true
    }];
    
    selectedUser = users[users.length-1];

    toast.push(m.team_manager_user_created(), {classes: ['toast-success']});
}

async function removeUser(){
    if (!selectedUser.id){
        return;
    }

    try { 
        // Check if user is the team owner
        if (authState.team.owner === selectedUser.email) {
            throw new Error(m.team_manager_owner_cannot_delete({ email: selectedUser.email }));
        }

        // Remove user document from Firestore
        await deleteDoc(doc(db, 'teams', authState.team.id, "users", selectedUser.id));

        // Update team members list
        const updatedMembers = authState.team.members.filter(member => member !== selectedUser.email);
        await updateDoc(doc(db, 'teams', authState.team.id), {
            members: updatedMembers
        });

        // Update local state
        updateTeam({
            ...authState.team,
            members: updatedMembers
        });

        toast.push(m.team_manager_user_removed_success(), {classes: ['toast-success']});
    } catch(err){
        console.log(err,err.message);
        toast.push(m.team_manager_remove_user_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
        return setup();
    }
}

async function saveUser(){
    try {
        
        if (!selectedUser.email){
            throw new Error(m.team_manager_email_required());
        }

        // Normalize email to lowercase
        selectedUser.email = selectedUser.email.toLowerCase();

        let existingUserCheck = users.filter(user => {
            return user.email === selectedUser.email;
        });

        if (existingUserCheck.length > 1){
            throw new Error(m.team_manager_email_exists());
        }

        // Check if user is the team owner
        if (authState.team.owner === selectedUser.email && !selectedUser.admin) {
            throw new Error(m.team_manager_owner_must_be_admin({ email: selectedUser.email }));
        }

        if (authState.team.owner === selectedUser.email && selectedUser.disable) {
            throw new Error(m.team_manager_owner_cannot_disable({ email: selectedUser.email }));
        }

        // Save user document to Firestore
        await setDoc(doc(db, 'teams', authState.team.id, "users", selectedUser.id), {
            id: selectedUser.id,
            firstName: selectedUser.firstName,
            lastName: selectedUser.lastName,
            email: selectedUser.email,
            admin: selectedUser.admin,
            disable: selectedUser.disable,
            role: selectedUser.role || "editor",
            groups: selectedUser.groups || []
        });

        // Update team members list if this is a new user
        if (!authState.team.members.includes(selectedUser.email)) {
            const updatedMembers = [...authState.team.members, selectedUser.email];
            await updateDoc(doc(db, 'teams', authState.team.id), {
                members: updatedMembers
            });

            updateTeam({
                ...authState.team,
                members: updatedMembers
            });
        }
        
        toast.push(m.team_manager_user_saved_success(), {classes: ['toast-success']});
    } catch(err){
        console.log(err, err.message);
        toast.push(m.team_manager_save_user_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
        return setup();
    }
}

async function createGroup() {
    groups = [...groups, {
        id: uuidv4(),
        name: "",
        description: "",
        members: [],
        new: true
    }];
    
    selectedGroup = groups[groups.length-1];
    toast.push(m.team_manager_group_created(), {classes: ['toast-success']});
}

async function saveGroup() {
    try {
        if (!selectedGroup.name) {
            throw new Error(m.team_manager_group_name_required());
        }

        let existingGroupCheck = groups.filter(group => {
            return group.name === selectedGroup.name && group.id !== selectedGroup.id;
        });

        if (existingGroupCheck.length > 0) {
            throw new Error(m.team_manager_group_name_exists());
        }

        // Save group document to Firestore
        await setDoc(doc(db, 'teams', authState.team.id, "groups", selectedGroup.id), {
            id: selectedGroup.id,
            name: selectedGroup.name,
            description: selectedGroup.description,
            members: selectedGroup.members || []
        });

        // Remove the 'new' flag since the group is now saved in the database
        if (selectedGroup.new) {
            const groupIndex = groups.findIndex(g => g.id === selectedGroup.id);
            if (groupIndex !== -1) {
                groups[groupIndex] = { ...groups[groupIndex], new: false };
                selectedGroup = groups[groupIndex];
            }
        }

        toast.push(m.team_manager_group_saved_success(), {classes: ['toast-success']});
    } catch(err) {
        console.log(err, err.message);
        toast.push(m.team_manager_save_group_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
        return setupGroups();
    }
}

async function removeGroup() {
    if (!selectedGroup.id) {
        return;
    }

    try { 
        // Delete group document from Firestore
        await deleteDoc(doc(db, 'teams', authState.team.id, "groups", selectedGroup.id));

        // Remove group from all users who are members
        const batch = writeBatch(db);
        for (const user of users) {
            if (user.groups && user.groups.includes(selectedGroup.id)) {
                const userRef = doc(db, 'teams', authState.team.id, "users", user.id);
                batch.update(userRef, {
                    groups: user.groups.filter(gId => gId !== selectedGroup.id)
                });
            }
        }
        await batch.commit();

        toast.push(m.team_manager_group_removed_success(), {classes: ['toast-success']});
    } catch(err) {
        console.log(err, err.message);
        toast.push(m.team_manager_remove_group_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
        return setupGroups();
    }
}

async function addUserToGroup(userId, groupId) {
    try {
        // Set loading state for this operation
        loadingUserOperations[`${userId}_${groupId}`] = true;
        
        // Update user document to add group
        const userRef = doc(db, 'teams', authState.team.id, "users", userId);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        const userGroups = userData.groups || [];
        
        if (!userGroups.includes(groupId)) {
            await updateDoc(userRef, {
                groups: [...userGroups, groupId]
            });
        }
        
        // Update group document to add member
        const groupRef = doc(db, 'teams', authState.team.id, "groups", groupId);
        const groupDoc = await getDoc(groupRef);
        const groupData = groupDoc.data();
        const groupMembers = groupData.members || [];
        
        if (!groupMembers.includes(userId)) {
            await updateDoc(groupRef, {
                members: [...groupMembers, userId]
            });
        }
        
        // Update local data structures
        const user = users.find(u => u.id === userId);
        if (user) {
            user.groups = [...(user.groups || []), groupId];
        }
        
        const group = groups.find(g => g.id === groupId);
        if (group) {
            const userEmail = users.find(u => u.id === userId)?.email;
            if (userEmail && !group.members.includes(userEmail)) {
                group.members = [...group.members, userEmail];
            }
        }
        
        toast.push(m.team_manager_user_added_to_group_success(), {classes: ['toast-success']});
    } catch(err) {
        console.log("Error adding user to group", err);
        toast.push(m.team_manager_add_user_to_group_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
        // Clear loading state
        loadingUserOperations[`${userId}_${groupId}`] = false;
    }
}

async function removeUserFromGroup(userId, groupId) {
    try {
        // Set loading state for this operation
        loadingUserOperations[`${userId}_${groupId}`] = true;
        
        // Update user document to remove group
        const userRef = doc(db, 'teams', authState.team.id, "users", userId);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        const userGroups = userData.groups || [];
        
        await updateDoc(userRef, {
            groups: userGroups.filter(gId => gId !== groupId)
        });
        
        // Update group document to remove member
        const groupRef = doc(db, 'teams', authState.team.id, "groups", groupId);
        const groupDoc = await getDoc(groupRef);
        const groupData = groupDoc.data();
        const groupMembers = groupData.members || [];
        
        await updateDoc(groupRef, {
            members: groupMembers.filter(uId => uId !== userId)
        });
        
        // Update local data structures
        const user = users.find(u => u.id === userId);
        if (user) {
            user.groups = (user.groups || []).filter(g => g !== groupId);
        }
        
        const group = groups.find(g => g.id === groupId);
        if (group) {
            const userEmail = users.find(u => u.id === userId)?.email;
            if (userEmail) {
                group.members = group.members.filter(m => m !== userEmail);
            }
        }
        
        toast.push(m.team_manager_user_removed_from_group_success(), {classes: ['toast-success']});
    } catch(err) {
        console.log("Error removing user from group", err);
        toast.push(m.team_manager_remove_user_from_group_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
        // Clear loading state
        loadingUserOperations[`${userId}_${groupId}`] = false;
    }
}

async function setup(){
    selectedUser = {};
    
    try {
        // Get users directly from Firestore
        const usersSnapshot = await getDocs(collection(db, 'teams', authState.team.id, "users"));
        users = [];
        usersSnapshot.forEach(doc => {
            users.push(doc.data());
        });

        sortUsers();
        
        // Also load groups when we load users
        await setupGroups();
    } catch(err) {
        console.error("Error loading users", err);
        toast.push(m.team_manager_load_users_failed({ error: err.message }), {classes: ['toast-danger']});
        users = [];
    }
}

async function setupGroups() {
    selectedGroup = {};
    try {
        // Get groups directly from Firestore
        const groupsSnapshot = await getDocs(collection(db, 'teams', authState.team.id, "groups"));
        groups = [];
        groupsSnapshot.forEach(doc => {
            groups.push(doc.data());
        });
        sortGroups();
    } catch(err) {
        console.error("Error loading groups", err);
        toast.push(m.team_manager_load_groups_failed({ error: err.message }), {classes: ['toast-danger']});
        groups = [];
    }
}

async function toggleAdPlugin(userId, currentStatus) {
    try {
        // Set loading state for this operation
        loadingPluginOperations[userId] = true;
        
        // Count how many AD licenses are currently in use
        const usedLicenses = users.filter(u => u.ad_plugin === true).length;
        const availableLicenses = authState.team.ad_plugin || 0;
        
        // If trying to enable and no licenses available
        if (!currentStatus && usedLicenses >= availableLicenses) {
            throw new Error(m.team_manager_ad_plugin_no_licenses({ count: availableLicenses }));
        }
        
        // Update user document to toggle AD plugin
        const userRef = doc(db, 'teams', authState.team.id, "users", userId);
        await updateDoc(userRef, {
            ad_plugin: !currentStatus
        });
        
        // Update local data structure
        const user = users.find(u => u.id === userId);
        if (user) {
            user.ad_plugin = !currentStatus;
        }
        
        toast.push(m.team_manager_ad_plugin_toggled({ status: !currentStatus ? m.team_manager_enabled() : m.team_manager_disabled() }), {classes: ['toast-success']});
    } catch(err) {
        console.log("Error toggling AD plugin", err);
        toast.push(m.team_manager_ad_plugin_update_failed({ error: err.message }), {classes: ['toast-danger']});
        
        // Revert the local state back to original status on error
        const user = users.find(u => u.id === userId);
        if (user) {
            user.ad_plugin = currentStatus;
        }
    } finally {
        // Clear loading state
        loadingPluginOperations[userId] = false;
    }
}

if (authState.user && authState.user.admin){
    dbWork = setup();
}
</script>
<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'teamManager' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="teamManagerTitle"
    aria-modal="true"
    tabindex="-1"
    id="TeamManagerModal">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="teamManagerTitle">{m.team_manager_title()}</h4>
                <button
                    type="button" 
                    class="btn-close"
                    aria-label={m.close()}
                    onclick={hideModal}
></button>
            </div>
            <div class="modal-body">
                {#if !authState.team}
                    {#await dbWork}
                        <div class="d-flex justify-content-center align-items-center p-3">
                            <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                        </div>       
                    {:then}
                        <div class="d-flex justify-content-center align-items-center p-3">
                            <div>
                                <p>{m.team_manager_create_team_prompt()}</p>
                                <button type="button" class="btn btn-primary w-100" onclick={() => {dbWork = createTeam()}}>{m.team_manager_create_team_button()}</button>
                            </div>                        
                        </div>
                    {/await}
                {:else if authState.user.admin}
                    <ul class="nav nav-tabs mb-3">
                        <li class="nav-item">
                            <button 
                                class="nav-link {activeTab === 'users' ? 'active' : ''}" 
                                onclick={() => activeTab = 'users'}>
                                {m.team_manager_users_tab()}
                            </button>
                        </li>
                        <li class="nav-item">
                            <button 
                                class="nav-link {activeTab === 'groups' ? 'active' : ''}" 
                                onclick={() => activeTab = 'groups'}>
                                {m.team_manager_groups_tab()}
                            </button>
                        </li>
                        {#if authState.team.ad_plugin && authState.team.ad_plugin > 0}
                        <li class="nav-item">
                            <button 
                                class="nav-link {activeTab === 'plugins' ? 'active' : ''}" 
                                onclick={() => activeTab = 'plugins'}>
                                {m.team_manager_plugins_tab()}
                            </button>
                        </li>
                        {/if}
                    </ul>
                    
                    {#if activeTab === 'users'}
                        <div class="row">
                            <div class="col-4">
                                <h4>{m.team_manager_users_heading()}
                                    <span class="badge rounded-pill bg-secondary d-xl-flex justify-content-xl-end align-items-xl-center float-end fs-6">{m.team_manager_user_count({ count: users.length })}</span>
                                </h4>
                                {#await dbWork}
                                    <div class="d-flex justify-content-center align-items-center p-3">
                                        <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                    </div>       
                                {:then}
                                    <select 
                                        id="userList"
                                        class="w-100" 
                                        size="15" 
                                        bind:value={selectedUser}
                                        aria-label={m.team_manager_select_user_aria()}>
                                        {#each users as user (user.id)}
                                            <option 
                                                class="{user.disable ? 'text-muted' : ''} {user.role === 'admin' ? 'text-info' : ''}"
                                                value={user}
                                                aria-label={formatUserOptionLabel(user)}
                                                title={formatUserOptionLabel(user)}
                                            >
                                                {formatUserName(user)}
                                            </option>
                                        {/each}
                                    </select>
                                    <div class="d-xl-flex justify-content-xl-end py-2">
                                        <div class="btn-group" role="group">
                                            <button title={m.team_manager_add_user_title()} class="btn btn-primary" type="button" onclick={() => {dbWork = createUser()}}>
                                                <i class="bi bi-plus-lg"></i> {m.team_manager_new_user_button()}
                                            </button>
                                            <button title={m.team_manager_remove_user_title()} class="btn btn-light" type="button" onclick={() => {dbWork = removeUser()}}>
                                                <i class="bi bi-dash-lg"></i> {m.team_manager_delete_user_button()}
                                            </button>
                                        </div>
                                    </div>
                                {/await}
                            </div>
                            <div class="col">
                                <div class="bg-secondary border rounded border-secondary shadow p-3">
                                    <form>
                                        <div class="mb-3">
                                            <label class="form-label" for="userFirstName">{m.team_manager_first_name_label()}</label>
                                            <input 
                                                id="userFirstName"
                                                class="form-control {!selectedUser.id ? 'text-muted bg-light' : ''}"
                                                type="text"
                                                placeholder={m.team_manager_first_name_placeholder()}
                                                disabled={!selectedUser.id}
                                                bind:value={selectedUser.firstName}
                                                aria-describedby="firstNameHelp"
                                            />
                                            <div id="firstNameHelp" class="form-text">{m.team_manager_first_name_help()}</div>
                                        </div>
                                        <div class="mb-3">
                                            <label class="form-label" for="UserLastNameInput">{m.team_manager_last_name_label()}</label>
                                            <input id="UserLastNameInput" class="form-control {!selectedUser.id ? 'text-muted bg-light' : ''}" type="text" placeholder={m.team_manager_last_name_placeholder()} disabled={!selectedUser.id} bind:value={selectedUser.lastName}/></div>
                                        <div class="mb-3">
                                            <label class="form-label" for="UserEmailInput">{m.team_manager_email_label()}</label>
                                            <input id="UserEmailInput" class="form-control {!selectedUser.id || !selectedUser.new ? 'text-muted bg-light' : ''}" type="email" placeholder={m.team_manager_email_placeholder()} disabled={!selectedUser.id || !selectedUser.new} bind:value={selectedUser.email}/>
                                        </div>
                                        
                                        <div class="mb-3">
                                            <label class="form-label" for="UserRolesInput">{m.team_manager_user_role_label()}</label>
                                            <select 
                                                id="UserRolesInput" 
                                                class="form-select {!selectedUser.id ? 'text-muted bg-light' : ''}" 
                                                disabled={!selectedUser.id} 
                                                bind:value={selectedUser.role}
                                                aria-describedby="roleHelp"
                                            >
                                                {#each availableRoles as role (role)}
                                                    <option value={role}>{getRoleLabel(role)}</option>
                                                {/each}
                                            </select>
                                            <div id="roleHelp" class="form-text">{m.team_manager_role_help()}</div>
                                        </div>
                                        
                                        <div class="mb-3">
                                            <label class="form-label" for="UserGroupsInput">{m.team_manager_groups_label()}</label>
                                            <div class="list-group">
                                                {#if selectedUser.id && groups.length > 0}
                                                    {#each groups as group (group.id)}
                                                        <div class="list-group-item d-flex justify-content-between align-items-center">
                                                            <span>{group.name}</span>
                                                            <div>
                                                                {#if group.members?.includes(selectedUser.email)}
                                                                    <button 
                                                                        type="button" 
                                                                        class="btn btn-sm btn-outline-danger"
                                                                        disabled={loadingUserOperations[`${selectedUser.id}_${group.id}`]}
                                                                        onclick={() => removeUserFromGroup(selectedUser.id, group.id)}
                                                                    >
                                                                        {#if loadingUserOperations[`${selectedUser.id}_${group.id}`]}
                                                                            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                        {:else}
                                                                            {m.remove()}
                                                                        {/if}
                                                                    </button>
                                                                {:else}
                                                                    <button 
                                                                        type="button" 
                                                                        class="btn btn-sm btn-outline-primary"
                                                                        disabled={loadingUserOperations[`${selectedUser.id}_${group.id}`]}
                                                                        onclick={() => addUserToGroup(selectedUser.id, group.id)}
                                                                    >
                                                                        {#if loadingUserOperations[`${selectedUser.id}_${group.id}`]}
                                                                            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                        {:else}
                                                                            {m.add()}
                                                                        {/if}
                                                                    </button>
                                                                {/if}
                                                            </div>
                                                        </div>
                                                    {/each}
                                                {:else if !selectedUser.id}
                                                    <div class="list-group-item text-muted">{m.team_manager_select_user_groups_prompt()}</div>
                                                {:else if groups.length === 0}
                                                    <div class="list-group-item text-muted">{m.team_manager_no_groups_available()}</div>
                                                {/if}
                                            </div>
                                        </div>
                                        
                                        <div class="form-check form-switch mb-3">
                                            <input class="form-check-input" type="checkbox" id="UseDisableCheck" disabled={!selectedUser.id} bind:checked={selectedUser.disable}/>
                                            <label class="form-check-label" for="UseDisableCheck">{m.team_manager_disable_account_label()}</label>
                                        </div>
                                        
                                        {#await dbWork}
                                            <div class="d-flex justify-content-center align-items-center p-3">
                                                <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                            </div>       
                                        {:then}
                                        <div class="d-xl-flex justify-content-xl-end">
                                            <div class="btn-group" role="group">
                                                <button class="btn btn-primary" type="button" disabled={!selectedUser.id} onclick={() => {dbWork = saveUser()}}>{m.team_manager_save_user_button()}</button>
                                                <button class="btn btn-outline-dark" type="button" disabled={!selectedUser.id} onclick={() => {selectedUser = {}; dbWork = setup()}}>{m.cancel()}</button>
                                            </div>
                                        </div>
                                        {/await}
                                    </form>
                                </div>
                            </div>
                        </div>
                    {:else if activeTab === 'groups'}
                        <div class="row">
                            <div class="col-4">
                                <h4>{m.team_manager_groups_heading()}
                                    <span class="badge rounded-pill bg-secondary d-xl-flex justify-content-xl-end align-items-xl-center float-end fs-6">{m.team_manager_group_count({ count: groups.length })}</span>
                                </h4>
                                {#await dbWork}
                                    <div class="d-flex justify-content-center align-items-center p-3">
                                        <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                    </div>       
                                {:then}
                                    <select 
                                        id="groupList"
                                        class="w-100" 
                                        size="15" 
                                        bind:value={selectedGroup}
                                        aria-label={m.team_manager_select_group_aria()}>
                                        {#each groups as group (group.id)}
                                            <option 
                                                value={group}
                                                aria-label={group.name}
                                            >
                                                {group.name || m.team_manager_unnamed_group()}
                                            </option>
                                        {/each}
                                    </select>
                                    <div class="d-xl-flex justify-content-xl-end py-2">
                                        <div class="btn-group" role="group">
                                            <button title={m.team_manager_add_group_title()} class="btn btn-primary" type="button" onclick={() => {dbWork = createGroup()}}>
                                                <i class="bi bi-plus-lg"></i> {m.team_manager_new_group_button()}
                                            </button>
                                            <button title={m.team_manager_remove_group_title()} class="btn btn-light" type="button" onclick={() => {dbWork = removeGroup()}}>
                                                <i class="bi bi-dash-lg"></i> {m.team_manager_delete_group_button()}
                                            </button>
                                        </div>
                                    </div>
                                {/await}
                            </div>
                            <div class="col">
                                <div class="bg-secondary border rounded border-secondary shadow p-3">
                                    <form>
                                        <div class="mb-3">
                                            <label class="form-label" for="groupName">{m.team_manager_group_name_label()}</label>
                                            <input 
                                                id="groupName"
                                                class="form-control {!selectedGroup.id ? 'text-muted bg-light' : ''}"
                                                type="text"
                                                placeholder={m.team_manager_group_name_placeholder()}
                                                disabled={!selectedGroup.id}
                                                bind:value={selectedGroup.name}
                                                aria-describedby="groupNameHelp"
                                            />
                                            <div id="groupNameHelp" class="form-text">{m.team_manager_group_name_help()}</div>
                                        </div>
                                        <div class="mb-3">
                                            <label class="form-label" for="groupDescription">{m.team_manager_group_description_label()}</label>
                                            <textarea 
                                                id="groupDescription" 
                                                class="form-control {!selectedGroup.id ? 'text-muted bg-light' : ''}" 
                                                rows="3" 
                                                placeholder={m.team_manager_group_description_placeholder()} 
                                                disabled={!selectedGroup.id} 
                                                bind:value={selectedGroup.description}
                                            ></textarea>
                                        </div>
                                        
                                        <div class="mb-3">
                                            <label class="form-label" for="UserGroupsInput">{m.team_manager_users_label()}</label>
                                            <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                                                {#if selectedGroup.new}
                                                    <div class="alert alert-info">
                                                        <i class="bi bi-info-circle"></i> {m.team_manager_save_group_before_users()}
                                                    </div>
                                                {:else}
                                                    <table class="table table-striped table-hover">
                                                        <thead style="position: sticky; top: 0; background-color: #fff; z-index: 1;">
                                                            <tr>
                                                                <th>{m.team_manager_table_name()}</th>
                                                                <th>{m.team_manager_table_email()}</th>
                                                                <th>{m.team_manager_table_action()}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {#if selectedGroup.id && users.length > 0}
                                                                {#each users as user (user.id)}
                                                                    <tr>
                                                                        <td>{user.firstName} {user.lastName}</td>
                                                                        <td>{user.email}</td>
                                                                        <td>
                                                                            {#if selectedGroup.members?.includes(user.email)}
                                                                                <button 
                                                                                    type="button" 
                                                                                    class="btn btn-sm btn-outline-danger"
                                                                                    disabled={loadingUserOperations[`${user.id}_${selectedGroup.id}`]}
                                                                                    onclick={() => removeUserFromGroup(user.id, selectedGroup.id)}
                                                                                >
                                                                                    {#if loadingUserOperations[`${user.id}_${selectedGroup.id}`]}
                                                                                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                                    {:else}
                                                                                        {m.remove()}
                                                                                    {/if}
                                                                                </button>
                                                                            {:else}
                                                                                <button 
                                                                                    type="button" 
                                                                                    class="btn btn-sm btn-outline-primary"
                                                                                    disabled={loadingUserOperations[`${user.id}_${selectedGroup.id}`]}
                                                                                    onclick={() => addUserToGroup(user.id, selectedGroup.id)}
                                                                                >
                                                                                    {#if loadingUserOperations[`${user.id}_${selectedGroup.id}`]}
                                                                                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                                    {:else}
                                                                                        {m.add()}
                                                                                    {/if}
                                                                                </button>
                                                                            {/if}
                                                                        </td>
                                                                    </tr>
                                                                {/each}
                                                            {:else if !selectedGroup.id}
                                                                <tr>
                                                                    <td colspan="3" class="text-center text-muted">{m.team_manager_select_group_members_prompt()}</td>
                                                                </tr>
                                                            {:else if users.length === 0}
                                                                <tr>
                                                                    <td colspan="3" class="text-center text-muted">{m.team_manager_no_users_available()}</td>
                                                                </tr>
                                                            {/if}
                                                        </tbody>
                                                    </table>
                                                {/if}
                                            </div>
                                        </div>
                                        
                                        {#await dbWork}
                                            <div class="d-flex justify-content-center align-items-center p-3">
                                                <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                            </div>       
                                        {:then}
                                        <div class="d-xl-flex justify-content-xl-end">
                                            <div class="btn-group" role="group">
                                                <button class="btn btn-primary" type="button" disabled={!selectedGroup.id} onclick={() => {dbWork = saveGroup()}}>{m.team_manager_save_group_button()}</button>
                                                <button class="btn btn-outline-dark" type="button" disabled={!selectedGroup.id} onclick={() => {selectedGroup = {}; dbWork = setupGroups()}}>{m.cancel()}</button>
                                            </div>
                                        </div>
                                        {/await}
                                    </form>
                                </div>
                            </div>
                        </div>
                    {:else if activeTab === 'plugins'}
                        <div class="row">
                            <div class="col-12">
                                <h4>{m.team_manager_plugins_heading()}</h4>
                                <p class="text-muted small">
                                    <i class="bi bi-info-circle"></i> 
                                    {m.team_manager_plugins_contact_support_text()}
                                    <a href="mailto:support@closedcaptioncreator.com">{m.team_manager_plugins_contact_support_email()}</a>
                                </p>
                                
                                {#await dbWork}
                                    <div class="d-flex justify-content-center align-items-center p-3">
                                        <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                    </div>       
                                {:then}
                                    <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
                                        <table class="table table-striped table-hover">
                                            <thead style="position: sticky; top: 0; background-color: #fff; z-index: 1; box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.1);">
                                                <tr>
                                                    <th>{m.team_manager_table_user()}</th>
                                                    <th>
                                                        {m.team_manager_audio_description_plugin()} 
                                                        <span class="badge bg-primary ms-2">
                                                            {m.team_manager_licenses_used({ used: users.filter(u => u.ad_plugin === true).length, total: authState.team.ad_plugin || 0 })}
                                                        </span>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {#each users as user (user.id)}
                                                    <tr>
                                                        <td>
                                                            <div>
                                                                <strong>{user.firstName} {user.lastName}</strong>
                                                                <br/>
                                                                <small class="text-muted">{user.email}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div class="form-check form-switch">
                                                                <input 
                                                                    class="form-check-input" 
                                                                    type="checkbox" 
                                                                    id="adPlugin_{user.id}"
                                                                    checked={user.ad_plugin === true}
                                                                    disabled={loadingPluginOperations[user.id]}
                                                                    onchange={() => toggleAdPlugin(user.id, user.ad_plugin === true)}
                                                                    aria-label={m.team_manager_toggle_ad_plugin_aria({ name: formatUserName(user) })}
                                                                />
                                                                <label class="form-check-label" for="adPlugin_{user.id}">
                                                                    {#if loadingPluginOperations[user.id]}
                                                                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                        <span class="ms-2">{m.team_manager_processing()}</span>
                                                                    {:else}
                                                                        {user.ad_plugin === true ? m.team_manager_enabled() : m.team_manager_disabled()}
                                                                    {/if}
                                                                </label>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                {/each}
                                            </tbody>
                                        </table>
                                    </div>
                                {/await}
                            </div>
                        </div>
                    {/if}
                {:else}
                    <div class="d-flex justify-content-center align-items-center p-3">
                        <div>
                            <p class="m-0">{m.team_manager_no_permission_title()}</p>
                            <p>{m.team_manager_no_permission_text()}</p>
                            <button type="button" class="btn btn-light w-100" onclick={hideModal}><i class="bi bi-x-lg"></i> {m.close()}</button>
                        </div>                        
                    </div>
                {/if}
            </div>
        </div>
    </div>
</div>